<template>
  <div class="process-container">
    <!-- 左侧导航栏 -->
    <StepNav 
      :steps="steps"
      @step-click="handleStepClick" 
    />

    <!-- 右侧主内容区 -->
    <div class="main-content">

      <!-- 成果库生成页面 -->
      <AchievementLibrary 
        v-show="showAchievementLibrary"
        :solution-data="achievementLibraryData"
        :project-id="projectId"
        @back-to-generated-solution="backToGeneratedSolution"
        @back="backToAchievementLibrary"
        @back-to-home="backToHome"
      />
      
      <!-- 生成的技术方案 -->
      <GeneratedSolution 
        v-show="showGeneratedSolution"
        :selected-tech-paths="selectedSolutionData.selectedTechPaths"
        :selected-patents-data="selectedSolutionData.selectedPatentsData"
        :solution-sections="selectedSolutionData.solutionSections"
        :reproduction-data="reproductionData"
        :is-get-generate="isGetGenerate"
        @back="backToAnalysisResult" 
        @back-to-edit="backToEdit"
        @enter-achievement-library="enterAchievementLibrary"
        @solution-generated="handleSolutionGenerated"
        @content-updated="handleContentUpdated"

      />
      
      <!-- 技术路径详情 -->
      <TechPathDetail 
        v-show="showTechPathDetail"
        :tech-path="currentTechPath"
        :detail-loading="detailLoading"
        :image-loading="detailImageLoading"
        @back="backToAnalysisResult"
      />
      
      <!-- 相似专利 -->
      <SimilarPatents 
        v-show="showSimilarPatents"
        :tech-path-description="similarPatentsParams.techPathDescription"
        :tech-path-id="similarPatentsParams.pathId"
        @back="backToAnalysisResult"
        @update:patents="similarPatentsData = $event"
        :problem-description="problemDescription"
        :project-id="projectId"
      />
      
      <!-- 分析过程和结果：始终渲染，根据条件显示 -->
      <!-- 分析过程组件 -->
      <AnalysisProcess 
        v-show="!showAchievementLibrary && !showGeneratedSolution && !showTechPathDetail && !showSimilarPatents"
        :is-loading="isLoading"
        :progress="progress"
        :remaining-time="remainingTime"
        :is-reading="isReading"
        :analysis-results="analysisResults"
        :current-streaming-index="currentStreamingIndex"
        :analysis-content="problemDescription"
        :collapse="collapseAnalysis"
        :reproduction-data="reproductionData"
        :project-id="projectId"
        @cancel="cancelAnalysis"
        @analysis-complete="handleAnalysisComplete"
      />
      
      <!-- 分析失败：重新分析入口 -->
      <div v-if="analysisError && !showAchievementLibrary && !showGeneratedSolution && !showTechPathDetail && !showSimilarPatents" class="analysis-error">
        <el-alert title="分析失败，未能生成有效内容" type="error" show-icon :closable="false">
          <p>模型多次未产出有效内容（可能网络波动或服务繁忙），请重试；若持续失败，可返回首页调整问题描述后重新开始。</p>
        </el-alert>
        <div class="analysis-error__actions">
          <el-button type="primary" :loading="isLoading" @click="retryAnalysis">
            <el-icon><Refresh /></el-icon>
            重新分析
          </el-button>
          <el-button @click="backToHome">返回首页</el-button>
        </div>
      </div>

      <!-- 分析结果组件：只在分析完毕后显示 -->
      <AnalysisResult 
        v-show="!isLoading && currentStreamingIndex === -1 && analysisResults.length > 0 && !showAchievementLibrary && !showGeneratedSolution && !showTechPathDetail && !showSimilarPatents"
        :added-count="0"
        :total-count="4"
        :analysis-content="problemDescription"
        :tech-paths="techPaths"
        :patents="similarPatentsData"
        :is-loading="techPathsLoading"
        :project-id="projectId"
        @generate-solution="generateSolution"
        @view-tech-path-detail="viewTechPathDetail"
        @view-similar-patents="viewSimilarPatents"
        @triplet-updated="handleTripletUpdated"
        :triplet="triplet"
      />
      
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import { 
  ArrowDown, 
  InfoFilled, 
  Plus, 
  View, 
  Document, 
  Loading, 
  Close, 
  CircleCheck,
  User,
  Refresh
} from '@element-plus/icons-vue'
import { useRoute, useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import StepNav from '@/components/StepNav.vue'
import AnalysisProcess from '@/components/AnalysisProcess.vue'
import AnalysisResult from '@/components/AnalysisResult.vue'
import GeneratedSolution from '@/components/GeneratedSolution.vue'
import AchievementLibrary from '@/components/AchievementLibrary.vue'
import TechPathDetail from '@/components/TechPathDetail.vue'
import SimilarPatents from '@/components/SimilarPatents.vue'
// 导入项目相关API
import { projectDetail, projectEdit } from '@/api/process'
// 结构化落库 API（静默写入，失败不影响页面）
import { replaceTechnicalPaths, updateTechnicalPath, listTechnicalPathsByProject, updateTechnicalPathStatus, getSolutionByProject, getSelectionDetailByProject, replaceAlternativeSolutions, replaceSolutions, replaceGeneratedSolutions, addArtifact } from '@/api/persistence'
const route = useRoute();
const router = useRouter();
// 项目id
const projectId = ref(route.query.id)
// 分析失败标记：模型多次未产出有效内容时展示"重新分析"入口
const analysisError = ref(false)
const processData = ref(null)
// 状态管理
const isLoading = ref(false)
const progress = ref(0)
const remainingTime = ref(0)
const isGetGenerate = ref(0)
// 步骤状态管理
const steps = ref([
  {
    title: '问题输入',
    subtitle: '明确需求与边界',
    status: 'completed'
  },
  {
    title: '技术路径分析',
    subtitle: '匹配可行技术路线',
    status: 'active'
  },
  {
    title: '解决方案生成',
    subtitle: '输出完整解决方案',
    status: 'inactive'
  }
])
//最终方案数据
const achievementLibraryData = ref(null)
// 分析结果数据
const analysisResults = ref([])
const currentStreamingIndex = ref(-1)
// 技术路径数据
const techPaths = ref([])
// 技术路径加载状态
const techPathsLoading = ref(false)
// 控制分析过程的收起状态
const collapseAnalysis = ref(false)
// 用于数据复现的分析数据
const reproductionData = ref(null)
// 问题理解层数据（从 IPT 首页进入时携带）
const problemUnderstanding = ref(null)

// 问题描述内容
const problemDescription = ref(route.query.problemDescription)
console.log('problemDescription:', problemDescription.value)
// 是否显示生成的技术方案
const showGeneratedSolution = ref(false)

// 是否显示成果库生成页面
const showAchievementLibrary = ref(false)

// 是否显示技术路径详情
const showTechPathDetail = ref(false)

// 是否显示相似专利
const showSimilarPatents = ref(false)

// 当前查看的技术路径数据
const currentTechPath = ref(null)
// 技术路径详情/配图加载状态（查看详情时用）
const detailLoading = ref(false)
const detailImageLoading = ref(false)
let detailRequestId = 0
let detailImageRequestId = 0

// 判断技术路径详情是否已加载（detailLoaded 标志由详情生成/回显时写入）
const isTechPathDetailLoaded = (path) => path?.detailLoaded === true

// 给技术路径列表注入稳定唯一 _uid（列表 key 用），避免 id 缺失/重复导致 Vue 组件复用崩溃
const ensureTechPathUids = (paths) => {
  if (!Array.isArray(paths)) return paths
  return paths.map((p, i) => {
    if (p && p._uid === undefined) {
      p._uid = p.id !== undefined && p.id !== null && p.id !== '' ? `id-${p.id}` : `idx-${i}-${Math.random().toString(36).slice(2, 8)}`
    }
    return p
  })
}

// 相似专利查询参数
const similarPatentsParams = ref({
  techPathDescription: '',
  patentSearchParams: {}
})

// 相似专利数据
const similarPatentsData = ref([])

// 选中的技术路径和专利数据
const selectedSolutionData = ref({
  selectedTechPaths: [],
  selectedPatentsData: [],
  solutionSections: []
})

// 统一的上传数据函数：project_content 只存「问题理解 + TRIZ分析全文」，其他数据走专属表
const uploadProjectData = async (status) => {
  if (!projectId.value) return
  
  try {
    // 准备要上传的数据：只留两样（问题理解层 + TRIZ分析全文）
    const projectContent = {
      problemUnderstanding: problemUnderstanding.value || reproductionData.value?.problemUnderstanding || null,
      analysisResults: analysisResults.value
    }
    
    // 调用项目编辑接口上传数据
    await projectEdit(projectId.value, JSON.stringify(projectContent),status)
    console.log('项目数据已成功上传', projectId.value)
  } catch (error) {
    console.error('上传项目数据失败:', error)
  }
}

// 更新步骤状态的函数
const updateStepStatus = (activeStepIndex) => {
  steps.value.forEach((step, index) => {
    if (index < activeStepIndex) {
      step.status = 'completed'
    } else if (index === activeStepIndex) {
      step.status = 'active'
    } else {
      step.status = 'inactive'
    }
  })
}

// 生成技术方案
const generateSolution = async (solutionData) => {
  if (solutionData) {
    // 先完成路径正式ID映射和勾选记录落库，再把数据交给方案页。
    // 否则用户立即点击引用路径时，可能把模型临时ID当成数据库ID，导致“路径不存在”。
    await persistAlternativeSolutions(
      projectId.value,
      solutionData.selectedTechPaths,
      solutionData.selectedPatentsData
    )
    const selectedPaths = solutionData.selectedTechPaths || []
    selectedPaths.forEach(path => {
      const dbId = path._dbId || techPathIdMap.value[path.id]
      if (dbId) path._dbId = dbId
    })
    const missingPaths = selectedPaths.filter(path => !path._dbId)
    if (missingPaths.length > 0) {
      const titles = missingPaths.map(path => path.title || path.id || '未命名路径').join('、')
      ElMessage.error(`技术路径保存未完成：${titles}。请返回分析结果后重试`)
      console.error('进入方案页前发现技术路径缺少正式ID:', missingPaths)
      return
    }
    selectedSolutionData.value = solutionData
    // 强制触发重新生成：先重置为0再设1，避免重复生成时 isGetGenerate 已是1导致 watch 不触发
    isGetGenerate.value = 0
    console.log('接收的解决方案数据:', solutionData)
    // 生成解决方案后上传数据
    uploadProjectData(2)
    // 触发方案生成（等子组件挂载/重置完成后）
    setTimeout(() => {
      isGetGenerate.value = 1
    }, 50)
  }
  showGeneratedSolution.value = true
  // 切换到第三步
  updateStepStatus(2)
}

// 返回修改
const backToEdit = () => {
  showGeneratedSolution.value = false
  // 返回时保持第三步
  updateStepStatus(2)
}

// 进入成果库生成
const enterAchievementLibrary = (data) => {
  achievementLibraryData.value = data
  console.log('data',achievementLibraryData)
  showAchievementLibrary.value = true
  showGeneratedSolution.value = false
  // 保持第三步
  updateStepStatus(2)
}

// 返回生成技术方案
const backToGeneratedSolution = () => {
  showAchievementLibrary.value = false
  // 保持第三步
  updateStepStatus(2)
}
const backToAchievementLibrary = () => {
  showAchievementLibrary.value = false
  showGeneratedSolution.value = true
  // 保持第三步
  updateStepStatus(2)
}

// 返回首页
const backToHome = () => {
  // 导航回首页
  router.push('/')
}

// 处理解决方案生成完成事件
const handleSolutionGenerated = async (solutionSections) => {
  console.log('解决方案生成完成，更新数据并上传', solutionSections)
  // 更新解决方案数据
  selectedSolutionData.value.solutionSections = solutionSections
  // 上传数据
  await uploadProjectData(3)
  // 结构化落库（静默）：最终方案写入 solutions 表
  persistSolutions(
    projectId.value,
    selectedSolutionData.value.selectedTechPaths,
    selectedSolutionData.value.selectedPatentsData,
    solutionSections,
    true
  )
}

// 处理生成方案内容更新
const handleContentUpdated = (updatedSolutionSections) => {
  // 更新选中的解决方案数据
  selectedSolutionData.value.solutionSections = updatedSolutionSections
  // 调用上传数据函数，状态为3（完成）
  uploadProjectData(3)
  // 结构化落库（静默）：编辑保存后重新落库（删旧插新，保证最新）
  persistSolutions(
    projectId.value,
    selectedSolutionData.value.selectedTechPaths,
    selectedSolutionData.value.selectedPatentsData,
    updatedSolutionSections
  )
}

// 处理三元组更新
const handleTripletUpdated = (updatedTriplet) => {
  // 更新三元组数据
  triplet.value = [{ what: updatedTriplet[0], action: updatedTriplet[1], outcome: updatedTriplet[2] }]
  // 调用上传数据函数，状态为2（进行中）
  uploadProjectData(2)
}

// 构造技术路径配图提示词（黑白专利附图风格）
const buildTechPathImagePrompt = (path) => {
  const technicalLogic = String(path?.technicalLogic || '').slice(0, 1200)
  const structurePlan = String(path?.structurePlan || '').slice(0, 1600)
  return `请根据以下技术方案生成一张黑白专利附图风格的技术结构示意图，只生成一张图。要求：白色背景、黑色线稿、清晰的剖视图或结构示意图、部件用数字编号标注、突出部件连接关系和工作路径，不要艺术化渲染，不要照片质感，不要生成任何可读文字、标题、图例或水印，避免乱码。

技术路径：${path?.title || ''}
方案摘要：${path?.description || ''}
技术逻辑：${technicalLogic}
结构方案：${structurePlan}`
}

// 异步生成技术路径配图（轮询任务结果，成功则更新路径 images）
const generateTechPathImage = async (path, requestId) => {
  const imageRequestId = ++detailImageRequestId
  detailImageLoading.value = true
  try {
    const startResponse = await fetch('/api/result/generate-tech-path-images', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ prompt: buildTechPathImagePrompt(path) })
    })
    if (!startResponse.ok) throw new Error(`图片任务提交失败，状态码: ${startResponse.status}`)
    const startData = await startResponse.json()
    if (!startData.taskId) throw new Error('图片任务未返回任务ID')

    const maxPolls = 90
    for (let attempt = 0; attempt < maxPolls; attempt += 1) {
      await new Promise((resolve) => setTimeout(resolve, 2000))
      if (requestId !== detailRequestId || imageRequestId !== detailImageRequestId) return

      const pollResponse = await fetch(`/api/result/tech-path-images/${encodeURIComponent(startData.taskId)}`)
      if (!pollResponse.ok) throw new Error(`图片任务查询失败，状态码: ${pollResponse.status}`)
      const pollData = await pollResponse.json()
      if (pollData.status === 'SUCCEEDED') {
        const images = Array.isArray(pollData.images) ? pollData.images.filter(Boolean).slice(0, 1) : []
        if (images.length > 0) {
          const updatedPath = { ...path, images }
          currentTechPath.value = updatedPath
          techPaths.value = techPaths.value.map((item) => item.id === updatedPath.id ? updatedPath : item)
          if (reproductionData.value) reproductionData.value.techPaths = techPaths.value
          await uploadProjectData(2)
          // 图片写库（静默）：否则 /solution 详情页从数据库读不到配图
          await persistTechPathDetail(updatedPath)
        }
        return
      }
      if (pollData.status === 'FAILED') {
        throw new Error(pollData.error || '视觉模型生成图片失败')
      }
    }
    throw new Error('图片生成超时')
  } catch (error) {
    console.error('生成技术路径图片失败:', error)
  } finally {
    if (requestId === detailRequestId && imageRequestId === detailImageRequestId) {
      detailImageLoading.value = false
    }
  }
}

// 查看技术路径详情
const viewTechPathDetail = async (techPath) => {
  const requestId = ++detailRequestId
  currentTechPath.value = techPath
  showTechPathDetail.value = true
  // 保持第二步
  updateStepStatus(1)

  const cachedPath = techPaths.value.find((path) => path.id === techPath.id) || techPath
  if (isTechPathDetailLoaded(cachedPath)) {
    currentTechPath.value = cachedPath
    detailLoading.value = false
    detailImageLoading.value = false
    if (!Array.isArray(cachedPath.images) || cachedPath.images.length === 0) {
      void generateTechPathImage(cachedPath, requestId)
    }
    return
  }

  // 内存中没有完整详情：先查数据库最新状态（可能在 /solution 详情页已生成并落库），
  // 数据库已有详情则直接展示，不重复调用生成接口
  const dbId = techPath._dbId || cachedPath._dbId || techPath.id || cachedPath.id
  if (dbId) {
    try {
      const dbResp = await fetch(`/innovation/innovation/technical-path/${dbId}`, {
        headers: { 'Content-Type': 'application/json' }
      })
      if (dbResp.ok) {
        const dbData = await dbResp.json()
        const dbPath = dbData.result || dbData
        if (dbPath && (dbPath.technicalLogic || dbPath.structuralScheme)) {
          const merged = { ...cachedPath, ...mapDbToFrontend(dbPath), detailLoaded: true }
          merged._uid = cachedPath._uid || techPath._uid || merged.id
          currentTechPath.value = merged
          techPaths.value = techPaths.value.map((path) => path.id === merged.id ? merged : path)
          if (reproductionData.value) reproductionData.value.techPaths = techPaths.value
          detailLoading.value = false
          if (!Array.isArray(merged.images) || merged.images.length === 0) {
            void generateTechPathImage(merged, requestId)
          }
          return
        }
      }
    } catch (e) {
      console.warn('查询数据库详情失败，改为重新生成:', e)
    }
  }

  currentTechPath.value = { ...cachedPath, detailLoaded: false }

  detailLoading.value = true
  detailImageLoading.value = false
  // 200 秒超时控制：模型生成详情慢/偶发空返回时不再无限转圈
  const controller = new AbortController()
  const timeoutTimer = setTimeout(() => controller.abort(), 200 * 1000)
  try {
    const analysisContent = analysisResults.value?.[0]?.fullContent || ''
    const response = await fetch('/api/result/generate-tech-path-detail', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ content: analysisContent, techPath }),
      signal: controller.signal
    })
    if (!response.ok) throw new Error(`详情接口请求失败，状态码: ${response.status}`)
    const data = await response.json()
    if (requestId !== detailRequestId) return

    const detailedPath = data.techPath || { ...techPath, detailLoaded: true }
    // 继承原路径的 _uid：保证列表 key 稳定，详情替换不会导致组件重建/错位
    detailedPath._uid = cachedPath._uid || techPath._uid || detailedPath._uid || detailedPath.id || undefined
    currentTechPath.value = detailedPath
    techPaths.value = techPaths.value.map((path) => path.id === detailedPath.id ? detailedPath : path)
    if (reproductionData.value) reproductionData.value.techPaths = techPaths.value
    await uploadProjectData(2)
    // 结构化落库（静默）：详情补全后更新对应技术路径记录
    await persistTechPathDetail(detailedPath)

    // 文字详情完成后立即解除详情加载状态
    detailLoading.value = false
    void generateTechPathImage(detailedPath, requestId)

  } catch (error) {
    console.error('获取技术路径详情失败:', error)
    // 超时/失败时明确提示，避免一直转圈无反馈
    if (error.name === 'AbortError') {
      ElMessage.error('详情生成超时（200秒），请重试')
    } else {
      ElMessage.error('详情生成失败：' + (error.message || '请重试'))
    }
  } finally {
    clearTimeout(timeoutTimer)
    if (requestId === detailRequestId) detailLoading.value = false
  }
}

// 返回分析结果页面
const backToAnalysisResult = () => {
  console.log('返回分析结果页面')
  // 使进行中的详情/图片请求失效，避免返回后旧请求回写页面
  detailRequestId += 1
  detailImageRequestId += 1
  showTechPathDetail.value = false
  showSimilarPatents.value = false
  showGeneratedSolution.value = false
  showAchievementLibrary.value = false
  currentTechPath.value = null
  detailLoading.value = false
  detailImageLoading.value = false
  isReading.value = 1
  // 不再用 reproductionData 快照覆盖 analysisResults/techPaths：
  // 两者都可能已被本次会话重新生成，覆盖会导致"返回后分析结果消失/只剩分析过程"。
  // （techPaths 已改存专属表，reproductionData 里没有；analysisResults 同理保持当前值）
  // 返回时保持第二步
  updateStepStatus(1)
}

// 查看相似专利：在独立新标签页打开，不替换当前技术路径分析页面
const viewSimilarPatents = (params) => {
  console.log('新标签页查看相似专利:', params)
  const dbPathId = params.pathId !== undefined
    ? (techPathIdMap.value[params.pathId] || params.pathId)
    : ''
  const query = new URLSearchParams({
    techPathDescription: params.techPathDescription || '',
    pathId: dbPathId == null ? '' : String(dbPathId),
    problemDescription: problemDescription.value || '',
    projectId: projectId.value == null ? '' : String(projectId.value)
  })
  window.open(`/similar-patents?${query.toString()}`, '_blank', 'noopener,noreferrer')
}
const triplet = ref(null)

// 前端临时技术路径ID -> 数据库正式ID 的映射（落库后建立，勾选/方案引用正式ID用）
const techPathIdMap = ref({})

// 修复模型输出的"伪JSON"（单引号/无引号键），转成标准数组（TRIZ原理用）
const parseTriz = (raw) => {
  if (Array.isArray(raw)) return raw
  if (!raw) return []
  let str = typeof raw === 'string' ? raw : JSON.stringify(raw)
  try {
    const parsed = JSON.parse(str)
    return Array.isArray(parsed) ? parsed : []
  } catch (e) {
    try {
      str = str
        .replace(/,\s*}/g, '}')
        .replace(/([{,]\s*)([a-zA-Z_][a-zA-Z0-9_]*)\s*:/g, '$1"$2":')
        .replace(/'/g, '"')
      const parsed = JSON.parse(str)
      return Array.isArray(parsed) ? parsed : []
    } catch (e2) {
      return []
    }
  }
}

// 数据库字段 -> 前端 techPath 对象（恢复/详情页数据复用）
const mapDbToFrontend = (p) => ({
  id: p.id,
  title: p.title,
  description: p.description || '',
  tags: Array.isArray(p.tags) ? p.tags : [],
  pros: Array.isArray(p.prosCons?.pros) ? p.prosCons.pros : [],
  cons: Array.isArray(p.prosCons?.cons) ? p.prosCons.cons : [],
  isRecommended: p.isRecommended === 1,
  progress: p.progress ?? 50,
  status: p.status ?? 0,
  solutionDescription: p.solutionDescription || '',
  patentSearchParams: p.patentSearchParams || {},
  technicalLogic: p.technicalLogic || '',
  structurePlan: p.structuralScheme || '',
  applicationScenarios: splitByNumber(p.usageScenarios),
  engineeringPoints: splitByNumber(p.engineeringPoints),
  riskMitigations: Array.isArray(p.risksMitigation) ? p.risksMitigation : [],
  trizPrinciples: parseTriz(p.trizPrinciplesUsed),
  principleSummary: p.principleSummary || '',
  images: Array.isArray(p.images) ? p.images : [],
  detailLoaded: !!(p.technicalLogic || p.structuralScheme)
})

// 长文本按序号切分成段落数组（兼容 \n、以及 "1." "2." 等序号），每个序号另起一段
const splitByNumber = (str) => {
  if (!str) return []
  let s = String(str)
  const lines = s.split(/\r?\n/).map(l => l.trim()).filter(Boolean)
  if (lines.length > 1) return lines
  const parts = s.split(/(?=\d{1,2}[\.、．]\s*)/).map(p => p.trim()).filter(Boolean)
  return parts.length > 1 ? parts : (s ? [s] : [])
}

// 前端 techPath 对象 -> 后端 TechnicalPaths 实体字段
const mapTechPathToBackend = (path = {}, projectId) => ({
  projectId,
  title: path.title || '',
  description: path.description || '',
  tags: Array.isArray(path.tags) ? path.tags : [],
  technicalLogic: path.technicalLogic || '',
  structuralScheme: path.structurePlan || '',
  usageScenarios: Array.isArray(path.applicationScenarios) ? path.applicationScenarios.join('\n') : '',
  risksMitigation: Array.isArray(path.riskMitigations) ? path.riskMitigations : [],
  prosCons: { pros: Array.isArray(path.pros) ? path.pros : [], cons: Array.isArray(path.cons) ? path.cons : [] },
  trizPrinciplesUsed: Array.isArray(path.trizPrinciples) ? path.trizPrinciples : [],
  principleSummary: path.principleSummary || '',
  isRecommended: path.isRecommended ? 1 : 0,
  progress: path.progress ?? 50,
  solutionDescription: path.solutionDescription || '',
  patentSearchParams: path.patentSearchParams || {},
  engineeringPoints: Array.isArray(path.engineeringPoints) ? path.engineeringPoints.join('\n') : (path.engineeringPoints || ''),
  images: Array.isArray(path.images) ? path.images : []
})

// 技术路径落库（静默）：删旧插新，建立 临时ID->正式ID 映射
const persistTechPaths = async (projectId, paths) => {
  if (!projectId || !Array.isArray(paths) || paths.length === 0) return
  try {
    const saved = await replaceTechnicalPaths(projectId, paths.map(p => mapTechPathToBackend(p, projectId)))
    if (Array.isArray(saved) && saved.length === paths.length) {
      const map = {}
      paths.forEach((p, i) => {
        // 后端返回的是重新查询的数据库实体，顺序可能与入参不同：优先按标题匹配，找不到再按位置兜底
        const hit = saved.find(s => s.title === p.title) || saved[i]
        // 数据库正式ID写回对象，供方案引用/详情跳转（/solution?pathId=）使用
        if (p.id !== undefined && hit?.id) map[p.id] = hit.id
        if (hit?.id) p._dbId = hit.id
      })
      techPathIdMap.value = { ...techPathIdMap.value, ...map }
      console.log('技术路径已落库，ID映射:', map)
    }
  } catch (e) {
    console.error('技术路径落库失败（静默）:', e)
  }
}

// 技术路径详情补全后，按正式ID更新对应记录（静默）
const persistTechPathDetail = async (path) => {
  const dbId = techPathIdMap.value[path.id]
  if (!dbId) return
  try {
    await updateTechnicalPath(dbId, mapTechPathToBackend(path, projectId.value))
  } catch (e) {
    console.error('技术路径详情落库失败（静默）:', e)
  }
}

// 确保 临时ID->正式ID 映射存在：内存里没有时，按标题从数据库重建（刷新页面后映射会丢）
const ensureTechPathIdMap = async (projectId, paths) => {
  const existingMap = {}
  for (const path of (paths || [])) {
    if (path.id !== undefined && path._dbId) existingMap[path.id] = path._dbId
  }
  if (Object.keys(existingMap).length > 0) {
    techPathIdMap.value = { ...techPathIdMap.value, ...existingMap }
  }
  const missing = (paths || []).filter(p => p.id !== undefined && !techPathIdMap.value[p.id])
  if (missing.length === 0) return
  try {
    const dbPaths = await listTechnicalPathsByProject(projectId)
    if (Array.isArray(dbPaths) && dbPaths.length > 0) {
      const map = {}
      for (const p of (paths || [])) {
        if (p.id === undefined) continue
        // 优先按标题匹配（标题一般唯一），匹配不到按顺序补
        const hit = dbPaths.find(d => d.title === p.title) 
        if (hit) {
          map[p.id] = hit.id
          // 数据库正式ID写回对象，供方案引用/详情跳转使用
          if (hit.id) p._dbId = hit.id
        }
      }
      techPathIdMap.value = { ...techPathIdMap.value, ...map }
      console.log('已从数据库重建ID映射:', map)
    }
  } catch (e) {
    console.error('重建ID映射失败（静默）:', e)
  }
}

// 勾选记录落库（静默）：type=1 路径 / type=2 专利（专利先去重落库拿正式ID）
const persistAlternativeSolutions = async (projectId, selectedTechPaths, selectedPatentsData) => {
  if (!projectId) return
  // 先确保路径的正式ID映射存在（刷新后内存映射会丢，从数据库按标题重建）
  await ensureTechPathIdMap(projectId, selectedTechPaths)
  const records = []
  try {
    // projectId 统一转数字（避免字符串传入非空约束/类型问题）
    const pid = Number(projectId)
    for (const path of (selectedTechPaths || [])) {
      const dbId = techPathIdMap.value[path.id]
      if (dbId) records.push({ projectId: pid, type: 1, referId: Number(dbId) })
    }
    for (const patent of (selectedPatentsData || [])) {
      // 专利已在 SimilarPatents 批量落库时拿到 _dbId（数据库ID）
      const patentId = patent._dbId || (patent.id && typeof patent.id === 'number' ? patent.id : null)
      if (patentId) records.push({ projectId: pid, type: 2, referId: Number(patentId) })
    }
    if (records.length > 0) {
      await replaceAlternativeSolutions(projectId, records)
      console.log('勾选记录已落库:', records)
    }
    // ② 更新技术路径勾选状态：勾选的 status=1，其余=0（静默）
    try {
      const selectedDbIds = (selectedTechPaths || [])
        .map(p => techPathIdMap.value[p.id])
        .filter(Boolean)
      if (selectedDbIds.length > 0) {
        await updateTechnicalPathStatus(projectId, selectedDbIds)
        console.log('技术路径勾选状态已更新:', selectedDbIds)
      }
    } catch (e) {
      console.error('更新技术路径勾选状态失败（静默）:', e)
    }
  } catch (e) {
    console.error('勾选记录落库失败（静默）:', e)
  }
}

// 最终方案落库（静默）：整篇一条，主路径填 source_path_id，其余路径/专利进 citations
const persistSolutions = async (projectId, selectedTechPaths, selectedPatentsData, solutionSections, aiGenerated = false) => {
  if (!projectId || !Array.isArray(solutionSections) || solutionSections.length === 0) return
  // 先确保路径的正式ID映射存在（刷新后内存映射会丢，从数据库按标题重建）
  await ensureTechPathIdMap(projectId, selectedTechPaths)
  try {
    const mainPath = selectedTechPaths?.[0]
    const sourcePathId = mainPath ? techPathIdMap.value[mainPath.id] : null
    // source_path_id 是数据库 NOT NULL 字段：拿不到正式ID时跳过落库，绝不传 null 触发完整性异常
    if (!sourcePathId) {
      console.warn('最终方案未落库：主路径缺少正式ID（映射未建立）', mainPath)
      return
    }
    const citationPaths = (selectedTechPaths || []).slice(1).map(p => ({ type: 'techPath', id: p.id, title: p.title, dbId: techPathIdMap.value[p.id] }))
    const citationPatents = (selectedPatentsData || []).map(p => ({ type: 'patent', title: p.title || p.t, externalId: p.externalId || p.p || p.pn }))
    const solution = {
      projectId,
      title: (mainPath && mainPath.title) || '最终解决方案',
      sourcePathId,
      backgroundProblem: (solutionSections.find(s => /背景|问题/i.test(s.title || '')) || {}).content || '',
      coreLogicFusion: (solutionSections.find(s => /核心|逻辑|融合/i.test(s.title || '')) || {}).content || '',
      structureHighlights: (solutionSections.find(s => /结构|实施/i.test(s.title || '')) || {}).content || '',
      keyParameters: (solutionSections.find(s => /参数/i.test(s.title || '')) || {}).content || '',
      engineeringPoints: (solutionSections.find(s => /工程/i.test(s.title || '')) || {}).content || '',
      risksMitigationFinal: (solutionSections.find(s => /风险|缓解/i.test(s.title || '')) || {}).content || '',
      problemTriad: triplet.value?.[0] || null,
      citations: [...citationPaths, ...citationPatents],
      patentId: null
    }
    const saveSolution = aiGenerated ? replaceGeneratedSolutions : replaceSolutions
    await saveSolution(projectId, [solution])
  } catch (e) {
    console.error('最终方案落库失败（静默）:', e)
  }
}
// 技术分析完成后调用
const handleAnalysisComplete = async (analysisResult) => {
  console.log('[恢复调试] handleAnalysisComplete 触发, isTechPathsLoaded=', isTechPathsLoaded.value, '已有techPaths=', techPaths.value.length, 'isRestoring=', isRestoring.value)
  // 恢复数据期间：忽略 AnalysisProcess 误发的 analysis-complete，避免重新生成技术路径
  if (isRestoring.value) {
    console.warn('[恢复调试] 恢复中，忽略 analysis-complete 事件')
    return
  }
  if(!isTechPathsLoaded.value) {
    techPathsLoading.value = false
    // 技术路径生成完成后，收起分析过程
    collapseAnalysis.value = true
      return
    }
  isLoading.value = false

  // 模型空返回/全部重试失败：不再拿空内容继续调技术路径接口（否则接口 400 且页面空白），
  // 明确提示用户并展示"重新分析"入口，避免流程卡死
  if (!analysisResult || !analysisResult.fullContent || !String(analysisResult.fullContent).trim()) {
    console.error('分析结果为空，终止流程')
    techPathsLoading.value = false
    collapseAnalysis.value = true
    analysisError.value = true
    ElMessage.error('分析未产出有效内容，请点击"重新分析"重试')
    return
  }

  console.log('技术分析完成，开始生成技术路径:', analysisResult)
  
  // 清空之前的分析结果，只保留最新的一个
  analysisResults.value = [analysisResult]

  // 分析过程（TRIZ全文）先落库，不等技术路径生成完；技术路径稍后单独落 technical_paths 表
  await uploadProjectData(2)

  // 调用技术路径分析接口
  try {
    techPathsLoading.value = true

    // 200 秒超时控制：模型/服务异常时不再无限等待（避免界面"卡死"无反馈）
    const controller = new AbortController()
    const timeoutTimer = setTimeout(() => controller.abort(), 200 * 1000)
    try {
      const response = await fetch('/api/result/generate-tech-paths', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ content: analysisResult.fullContent }),
        signal: controller.signal
      })
      
      if (!response.ok) {
        throw new Error(`API请求失败，状态码: ${response.status}`)
      }
      
      const data = await response.json()
      console.log('技术路径分析结果:', data)
      
      // 更新技术路径数据
      if (data.techPaths && Array.isArray(data.techPaths) && data.techPaths.length > 0) {
        ensureTechPathUids(data.techPaths)
        techPaths.value = data.techPaths
        triplet.value = data.triplet
        // 结构化落库（静默）：技术路径写入 technical_paths 表，并建立 临时ID->正式ID 映射
        await persistTechPaths(projectId.value, data.techPaths)
      } else {
        // 接口 200 但没返回技术路径：不再静默空白，明确提示用户重试
        throw new Error('模型未返回技术路径数据，请重试')
      }
    } finally {
      clearTimeout(timeoutTimer)
    }
  } catch (error) {
    console.error('获取技术路径分析失败:', error)
    ElMessage.error('技术路径生成失败：' + (error.name === 'AbortError' ? '超时（200秒），请重试' : (error.message || '请重试')))
  } finally {
    techPathsLoading.value = false
    // 技术路径生成完成后，收起分析过程
    collapseAnalysis.value = true
    // 兜底再存一次分析过程（幂等；分析过程已在调用技术路径前存过）
    await uploadProjectData(2)
  }
}
const isTechPathsLoaded = ref(true)
const isReading = ref(0)
// 恢复中标志：onMounted 恢复数据期间，忽略 AnalysisProcess 误发的 analysis-complete 事件
const isRestoring = ref(false)
// 初始化
  onMounted(async () => {
    // 不再使用模拟分析，直接通过AnalysisProcess组件调用API
    isLoading.value = false
    problemDescription.value = route.query.problemDescription
    // 标记恢复中：期间 AnalysisProcess 的 analysis-complete 事件不触发技术路径生成
    isRestoring.value = true
    // 如果有projectId，调用projectDetail接口
    if (projectId.value) {
      try {
        isLoading.value = true
        const projectData = await projectDetail(projectId.value)
        // 获取项目状态
        const projectStatus = projectData.projectStatus
        // 检查projectContent字段是否有值
        if (projectData && projectData.projectContent) {
          try {
            // 尝试解析projectContent为JSON
            const projectContent = typeof projectData.projectContent === 'string' 
              ? JSON.parse(projectData.projectContent) 
              : projectData.projectContent
            console.log(projectContent,'projectContent')
            // 设置复现数据，由AnalysisProcess组件处理
            reproductionData.value = projectContent
            // 根据projectStatus状态值进行不同处理
            if (projectStatus == 1 ||projectStatus ===2) {
              // 如果projectContent包含analysisResults，直接使用
              if (projectContent.analysisResults) {
                // 设置分析结果
                analysisResults.value = projectContent.analysisResults
                
                // 技术路径从专属表读（project_content 不再存 techPaths）
                let dbPaths = []
                let techPathsLoadError = false
                try {
                  dbPaths = await listTechnicalPathsByProject(projectId.value)
                  console.log('[恢复调试] listTechnicalPathsByProject 返回:', JSON.stringify(dbPaths)?.slice(0, 200) ?? 'null')
                  // 接口返回非数组（如 500 时 resolve undefined）也视为读取失败
                  if (!Array.isArray(dbPaths)) {
                    techPathsLoadError = true
                    dbPaths = []
                  }
                } catch (e) {
                  techPathsLoadError = true
                  console.error('[恢复调试] 读取技术路径异常:', e)
                }
                console.log('[恢复调试] projectStatus=', projectStatus, 'dbPaths长度=', Array.isArray(dbPaths) ? dbPaths.length : '非数组', 'techPathsLoadError=', techPathsLoadError)
                if (Array.isArray(dbPaths) && dbPaths.length > 0) {
                  // 数据库字段 -> 前端 techPath 对象（还原展示用）
                  const frontPaths = dbPaths.map(p => mapDbToFrontend(p))
                  // 建立 临时ID->正式ID 映射（前端id=数据库id）
                  const map = {}
                  dbPaths.forEach(p => { map[p.id] = p.id })
                  techPathIdMap.value = map
                  ensureTechPathUids(frontPaths)
                  techPaths.value = frontPaths

                  // 勾选记录从新表读（join 专利/路径详情）
                  const selections = await getSelectionDetailByProject(projectId.value)
                  if (Array.isArray(selections) && selections.length > 0) {
                    const selPaths = selections.filter(s => s.type === 1)
                    const selPatents = selections.filter(s => s.type === 2)
                    // 回显勾选状态：status=1 的路径
                    frontPaths.forEach(p => {
                      if (selPaths.some(s => Number(s.referId) === Number(p.id))) {
                        p.isAddedToLibrary = true
                      }
                    })
                    selectedSolutionData.value.selectedTechPaths = frontPaths.filter(p => p.isAddedToLibrary)
                    // 专利：勾选记录只有编号+标题，详情可以从 external_patents 查（简化：用 detail 接口返回的字段）
                    selectedSolutionData.value.selectedPatentsData = selPatents.map(s => ({
                      title: s.title,
                      externalId: s.externalId,
                      abstract: s.abstract,
                      _dbId: Number(s.referId),
                      isAddedToLibrary: true,
                      progress: s.progress ?? 50
                    }))
                  }

                  isTechPathsLoaded.value = false
                  // 技术路径生成完成后，收起分析过程
                  collapseAnalysis.value = true
                  // 不进行过程分析，直接设置加载状态为false
                  isLoading.value = false
                  isReading.value = 1
                  // 恢复完成：取消恢复中标志
                  isRestoring.value = false
                } else {
                  // 读取失败或确实没有技术路径
                  if (techPathsLoadError) {
                    // 读取接口失败：不重跑，避免死循环；保持已分析状态，用户可手动重新分析
                    console.warn('[恢复调试] 技术路径读取失败，跳过重新生成')
                    isLoading.value = false
                    isReading.value = 1
                  } else {
                    // 确实没有技术路径（第一次分析中断）：重新生成
                    console.log('[恢复调试] 复现数据中没有技术路径，进行技术路径接口请求')
                    isLoading.value = false
                    isReading.value = 1
                    const analysisResult = projectContent.analysisResults[0]
                    if (analysisResult && analysisResult.fullContent) {
                      await handleAnalysisComplete(analysisResult)
                    }
                  }
                }
              } else {
                // 没有analysisResults，进行分析过程
                console.log('复现数据中没有分析结果，进行分析过程')
                isLoading.value = true
                isReading.value = 2
              }
            } else if (projectStatus === 3) {
              // 状态为3：步骤显示为3，显示GeneratedSolution页面
              console.log('项目状态为3，显示生成的技术方案')
              
              // 不进行过程分析，直接设置加载状态为false
              isLoading.value = false
              // 更新步骤状态为第三步
              updateStepStatus(2)
              
              // 注意：isGetGenerate=2 与 showGeneratedSolution=true 必须放在所有异步加载
              // （技术路径/方案/勾选记录）之后，否则 GeneratedSolution 的 watcher 会在
              // solutionSections 还没从数据库加载回来时就触发，导致方案章节不渲染。
              
              // 同时恢复分析结果与技术路径，保证从方案页"返回分析结果"仍有内容可看
              if (projectContent && Array.isArray(projectContent.analysisResults)) {
                analysisResults.value = projectContent.analysisResults
              }
              try {
                const dbPaths = await listTechnicalPathsByProject(projectId.value)
                if (Array.isArray(dbPaths) && dbPaths.length > 0) {
                  const map = {}
                  const frontPaths = dbPaths.map(p => {
                    map[p.id] = p.id
                    return mapDbToFrontend(p)
                  })
                  techPathIdMap.value = map
                  ensureTechPathUids(frontPaths)
                  techPaths.value = frontPaths
                  // 回显勾选状态：status=1 的路径标记为已入库
                  try {
                    const selections = await getSelectionDetailByProject(projectId.value)
                    if (Array.isArray(selections) && selections.length > 0) {
                      const selPaths = selections.filter(s => s.type === 1)
                      frontPaths.forEach(p => {
                        if (selPaths.some(s => Number(s.referId) === Number(p.id))) {
                          p.isAddedToLibrary = true
                        }
                      })
                      selectedSolutionData.value.selectedTechPaths = frontPaths.filter(p => p.isAddedToLibrary)
                    }
                  } catch (e) {
                    console.error('回显技术路径勾选状态失败（静默）:', e)
                  }
                }
              } catch (e) {
                console.error('状态3恢复技术路径失败（静默）:', e)
              }
              
              // 方案从专属表读（还原章节数组 + triplet）
              try {
                const solData = await getSolutionByProject(projectId.value)
                if (solData && Array.isArray(solData.solutionSections)) {
                  selectedSolutionData.value.solutionSections = solData.solutionSections
                  if (solData.triplet && !Array.isArray(solData.triplet)) {
                    triplet.value = [solData.triplet]
                  }
                }
              } catch (e) {
                console.error('读取方案失败（静默）:', e)
              }
              // 勾选记录从新表读
              try {
                const selections = await getSelectionDetailByProject(projectId.value)
                if (Array.isArray(selections)) {
                  selectedSolutionData.value.selectedTechPaths = selections.filter(s => s.type === 1).map(s => ({
                    title: s.title,
                    description: s.description || '',
                    progress: s.progress ?? 50,
                    isAddedToLibrary: true,
                    _dbId: Number(s.referId)
                  }))
                  selectedSolutionData.value.selectedPatentsData = selections.filter(s => s.type === 2).map(s => ({
                    title: s.title,
                    externalId: s.externalId,
                    abstract: s.abstract,
                    _dbId: Number(s.referId),
                    isAddedToLibrary: true,
                    progress: s.progress ?? 50
                  }))
                }
              } catch (e) {
                console.error('读取勾选记录失败（静默）:', e)
              }
              // 所有异步加载完成后再通知 GeneratedSolution 展示已存方案（避免空方案渲染）
              isGetGenerate.value = 2
              showGeneratedSolution.value = true
            }
          } catch (parseError) {
            console.error('解析projectContent失败:', parseError)
            // 解析失败，继续进行正常的数据分析
            isLoading.value = true
            isReading.value = 2
          }
        } else {
          console.log('projectContent为空，开始正常数据分析')
          // projectContent为空，继续进行正常的数据分析
          isLoading.value = true
          isReading.value = 2
        }
      } catch (error) {
        console.error('调用projectDetail接口失败:', error)
        // 接口调用失败，继续进行正常的数据分析
        isLoading.value = true
        isReading.value = 2
      }
    }
    // onMounted 全部结束：取消恢复中标志（之后 analysis-complete 事件正常处理）
    isRestoring.value = false
    // 恢复期间可能已把 isTechPathsLoaded 置为 false（防止恢复时重复生成），
    // 页面就绪后必须重置，否则之后真正的重新分析会被 handleAnalysisComplete 提前拦截、结果永远为空
    isTechPathsLoaded.value = true
  })

// 取消分析
const cancelAnalysis = () => {
  isLoading.value = false
  console.log('分析已取消')
}

// 重新分析：复位后重新触发 AnalysisProcess 的分析（isReading 0→2 保证 watch 触发）
const retryAnalysis = () => {
  analysisError.value = false
  isLoading.value = true
  isReading.value = 0
  setTimeout(() => {
    isReading.value = 2
  }, 0)
}

// 步骤点击处理
const handleStepClick = (step, index) => {
  console.log('点击了步骤:', step, index)
  // 这里可以添加步骤切换的逻辑
}
</script>

<style scoped lang="scss">
.process-container {
  display: flex;
  height: calc(100vh - 60px);
  width: 100%;
  background-color: #f5f5f5;
  box-sizing: border-box;
}

/* 分析失败重试卡片 */
.analysis-error {
  background: #fff;
  border: 1px solid #fde2e2;
  border-radius: 8px;
  padding: 20px;
  margin-top: 16px;

  p {
    margin: 6px 0 0;
    color: #666;
    font-size: 13px;
    line-height: 1.6;
  }

  &__actions {
    display: flex;
    gap: 12px;
    margin-top: 16px;
  }
}



/* 右侧主内容区 */
.main-content {
  width: 1200px;
  padding: 24px;
  overflow-y: auto;
  box-sizing: border-box;
  margin: 0 auto;
}

/* 顶部导航 */
.top-nav {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 30px;
  background-color: #fafafa;
  border-bottom: 1px solid #e8e8e8;
  
  :deep(.el-breadcrumb) {
    font-size: 14px;
    color: #666;
    
    a {
      color: #666;
      text-decoration: none;
      
      &:hover {
        color: #1890ff;
      }
    }
  }
}

.enter-btn {
  padding: 6px 16px;
  font-size: 14px;
  background-color: #1890ff;
  border-color: #1890ff;
  
  &:hover {
    background-color: #40a9ff;
    border-color: #40a9ff;
  }
}

/* 通用样式 */
.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  
  h3 {
    font-size: 18px;
    font-weight: 600;
    color: #333;
    margin: 0;
  }
}


</style>